import { coalesceNonEmpty } from '../string/coalesceNonEmpty.ts';

/**
 * Which of a taxon's three name fields to read, and in what order.
 *
 * A Sylius taxon carries `metaTitle`, `name` and `nameInTaxonList`, populated
 * independently by editors, so "what is this called" has two answers depending
 * on who is asking — a crawler reading a `title` attribute, or a shopper
 * reading a row. Resolved once here because a navigation asks in several places
 * and will otherwise disagree with itself: the same taxon reaches a `title`
 * attribute through one order and a visible label through another, and neither
 * call site says so.
 *
 * Both resolvers fall through empty strings, not only missing fields — an
 * editor who cleared a field means the same thing as one who never filled it.
 */

/** The three name fields, structurally — each tenant's decoded taxon satisfies it. */
export type TaxonNameFields = {
  readonly metaTitle?: string | null;
  readonly name?: string | null;
  readonly nameInTaxonList?: string | null;
};

/**
 * The name for a machine to read — a `title` attribute, a document title.
 * Prefers the field an editor wrote FOR that purpose, which is the long one.
 */
export const resolveTaxonNameForMetadata = (taxon: TaxonNameFields): string =>
  coalesceNonEmpty(taxon.metaTitle, taxon.name, taxon.nameInTaxonList) ?? '';

/**
 * The name a shopper reads in a list of taxons. Prefers the shortened form an
 * editor wrote for exactly that, and never the meta title, which is written to
 * be long.
 */
export const resolveTaxonNameForDisplay = (taxon: TaxonNameFields): string =>
  coalesceNonEmpty(taxon.nameInTaxonList, taxon.name) ?? '';
